import { PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Select, Space, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import ZTable, { ITableRef } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import Helmet from 'react-helmet';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'emailTemplate_Title';

const EmailTemplateTab = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<string[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const getEmailTemplateNames = () => {
    apiSdk.EmailTemplateService.emailTemplateNames()
      .then(response => {
        setEmailTemplates(response);
      })
      .catch(error => {
        message.error(error);
      });
  };

  const createEmailTemplate = values => {
    setLoading(true);
    apiSdk.EmailTemplateService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const columns = React.useMemo(() => {
    return [
      {
        title: t('emailTemplate_NameEmailTemplate'),
        dataIndex: 'name',
      },
      {
        title: t('emailTemplate_Subject'),
        dataIndex: 'subject',
      },
      {
        title: t('emailTemplate_CultureCode'),
        dataIndex: 'cultureCode',
      },
      {
        title: t('emailTemplate_Attachment'),
        dataIndex: 'includeAttachment',
        render: (value: any) => {
          return value ? <Tag color="#108ee9">{t('Yes')}</Tag> : <Tag color="#f50">No</Tag>;
        },
      },
      {
        title: t('emailTemplate_Active'),
        dataIndex: 'enabled',
        render: (value: any) => {
          return value ? <Tag color="#108ee9">{t('Yes')}</Tag> : <Tag color="#f50">No</Tag>;
        },
      },
    ];
  }, [emailTemplates]);

  const updateEmailTemplate = values => {
    setLoading(true);
    apiSdk.EmailTemplateService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getEmailTemplateNames();
  }, []);

  return (
    <div>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          tableRef={zTb}
          columns={columns}
          primaryKey="id"
          url="/notifications/email-templates/get-email-templates"
          bordered
          order_by="Name"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            form.resetFields();
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue(record);
          }}
          allowDelete={true}
          delete_url="/notifications/email-templates/delete"
          extraBtns={[
            <Button
              key="create"
              type="primary"
              className="nta-create-btn"
              icon={<PlusCircleOutlined />}
              onClick={() => {
                form.resetFields();
                setIsNewForm(true);
                setFormVisible(true);
              }}
            >
              {t('createNew')}
            </Button>,
          ]}
        ></ZTable>
        <Modal
          className="nta-custom-form"
          visible={formVisible}
          maskClosable={false}
          style={{ top: 10 }}
          title={isNewForm ? t('emailTemplate_CreateEmailTemplate') : t('emailTemplate_UpdateEmailTemplate')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          width={ModalWidth.extraLarge}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEmailTemplate(values);
                } else {
                  createEmailTemplate(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="vertical">
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={16}>
              <Col md={10}>
                <Form.Item name="name" label={t('emailTemplate_NameEmailTemplate')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select placeholder={t('emailTemplate_NameEmailTemplatePlaceholder')}>
                    {emailTemplates.map(k => (
                      <Select.Option key={k} value={k || ''}>
                        {emailTemplates[k]}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="cultureCode" label={t('emailTemplate_Attachment_CultureCode')}>
                  <Select>
                    <Select.Option value="en">English</Select.Option>
                    <Select.Option value="fr">Français</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="toEmails" label={t('emailTemplate_EmailTemplate_ToEmail')}>
                  <Input />
                </Form.Item>
                <Form.Item name="ccEmails" label={t('emailTemplate_EmailTemplate_CcEmail')}>
                  <Input />
                </Form.Item>
                <Form.Item name="bccEmails" label={t('emailTemplate_EmailTemplate_BccEmail')}>
                  <Input />
                </Form.Item>
                <Space>
                  <Form.Item name="includeAttachment" valuePropName="checked">
                    <Checkbox>{t('emailTemplate_Attachment')}</Checkbox>
                  </Form.Item>
                  <Form.Item name="enabled" valuePropName="checked">
                    <Checkbox>{t('emailTemplate_Active')}</Checkbox>
                  </Form.Item>
                </Space>
              </Col>
              <Col md={14}>
                <Form.Item name="subject" label={t('emailTemplate_Subject')}>
                  <Input />
                </Form.Item>
                <Form.Item name="body" label={t('emailTemplate_Body')}>
                  <Input.TextArea rows={20} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};
export default EmailTemplateTab;
